import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  makeStyles,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Text,
  Image,
  tokens,
  Caption1,
  MenuDivider,
  Body2,
  Body1,
  Subtitle2,
  shorthands,
} from "@fluentui/react-components";
import { logger } from "../../utils/logger";
import { useAddInStore, useTokenStore } from "../../hooks";
import { isString } from "lodash";
import {
  ArrowLeftRegular,
  CalendarLtrRegular,
  LinkRegular,
  PeopleTeamRegular,
} from "@fluentui/react-icons";
import {
  cadchatService,
  loginWithAuth0,
  logoutFromAuth0,
  OfficeService,
  refreshAuth0Token,
  showGettingStartedModal,
  supabaseApi,
} from "../../services";
import { Token } from "../../models/authentication";

interface AuthGuardProps {
  children: ReactNode;
}

/** create styles for the AuthGuard Component */
const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflowY: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    paddingTop: "15px",
    paddingLeft: "0",
    paddingRight: "10px",
    marginBottom: "10px",
  },
  logo: {
    height: "50px",
    marginBottom: "20px",
  },
  header_logo: {
    paddingLeft: "10px",
  },
  user_avatar: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  progress_container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    height: "65vh",
    justifyContent: "center",
    gap: "10px",
  },
  error_message: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: "10px",
  },
  login_button: {
    minWidth: "200px",
    fontWeight: 400,
  },
  login_message: {
    marginBottom: "25px",
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    "> button": {
      flex: "1 auto",
      width: "100%",
    },
  },
  welcome: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "16px",
    height: "100vh",
    boxSizing: "border-box",
    overflowY: "auto",
    padding: "32px 20px",
    textAlign: "center",
  },
  welcome_logo: {
    height: "34px",
  },
  tagline: {
    color: tokens.colorNeutralForeground1,
  },
  welcome_intro: {
    color: tokens.colorNeutralForeground2,
    lineHeight: "20px",
    maxWidth: "320px",
  },
  feature_card: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
    maxWidth: "320px",
    boxSizing: "border-box",
    padding: "16px",
    textAlign: "left",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
  },
  feature_row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  feature_icon: {
    flexShrink: 0,
    fontSize: "20px",
    color: tokens.colorBrandForeground1,
    marginTop: "1px",
  },
  feature_text: {
    color: tokens.colorNeutralForeground2,
    lineHeight: "19px",
  },
  sign_in_button: {
    width: "100%",
    maxWidth: "280px",
    fontWeight: 600,
  },
  sign_in_hint: {
    color: tokens.colorNeutralForeground3,
  },
});

const FEATURES = [
  {
    icon: <CalendarLtrRegular />,
    text: "Schedule CADchat meetings right from your Outlook calendar.",
  },
  {
    icon: <LinkRegular />,
    text: "Join links are added to your invites automatically.",
  },
  {
    icon: <PeopleTeamRegular />,
    text: "Keep your team in sync without switching between tools.",
  },
];

export const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const styles = useStyles();

  // used to store error messages
  const [authError, setAuthError] = useState<Error | undefined>(undefined);

  // indicates authentication is in progress
  const isAuthenticationInProgress = useRef<boolean>(false);

  // indicates that the token is being refreshed
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // indicates that the Office library is ready
  const [isOfficeReady, setIsOfficeReady] = useState<boolean>(false);

  // indicates that the current users avatar url
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  // indicates the current view
  const currentView = useAddInStore((state) => state.currentView);

  // used to set the current view
  const setCurrentView = useAddInStore((state) => state.setCurrentView);

  /** the current user account */
  const user = useAddInStore((state) => state.user);

  /** used to set the current user account */
  const setUser = useAddInStore((state) => state.setUser);

  /** used to reset the store on logout */
  const resetAddInState = useAddInStore((state) => state.resetState);

  // indicates the current user token
  const token = useTokenStore((state) => state.token);

  // used to set the current user token
  const setToken = useTokenStore((state) => state.setToken);

  // used to clear the current user token
  const clearToken = useTokenStore((state) => state.removeToken);

  // used to clear the current user token
  const resetTokenState = useTokenStore((state) => state.resetState);

  // used to check and see if the current user token is expired
  const isTokenExpired = useTokenStore((state) => state.isTokenExpired);

  // used to indicates the token isn't refreshed
  const tokenRefreshed = useTokenStore((state) => state.tokenRefreshed);

  // used to indicates the token isn't refreshed
  const setTokenRefreshed = useTokenStore((state) => state.setTokenRefreshed);

  // indicates that the current user is authenticated
  const isAuthenticated = useTokenStore((state) => state.isAuthenticated);

  // use to set the organization in the state
  const setOrganizations = useAddInStore((state) => state.setOrganizations);

  // use to set the organization in the state
  const selectedOrganizationId = useAddInStore((state) => state.selectedOrganizationId);

  // indicates the current status of the users onboarding progress
  const onboardingProgress = useAddInStore((state) => state.onboardingProgress);

  // indicates that this event already has a meeting
  const meeting = useAddInStore((state) => state.meeting);

  // Office service instance for resetting CADchat staging on navigation. AuthGuard sits outside the
  // OfficeItemProvider, but OfficeService operates on the live Outlook item, so a local instance is
  // fine here.
  const officeService = useMemo(() => new OfficeService(), []);

  /**
   * Handles the Back navigation. When leaving a staging view we reset any pending CADchat meeting,
   * since the user may be switching between creating a new space and adding to an existing one and
   * we don't want the previous staging to leave the Add button disabled.
   */
  const handleBack = useCallback(async () => {
    if (currentView === "create_space" || currentView === "add_to_existing_space") {
      try {
        await officeService.removePendingCADchatMeetingDetails();
      } catch (error) {
        logger.error("Error resetting CADchat staging on back:", error);
      }
    }
    setCurrentView("default");
  }, [currentView, officeService, setCurrentView]);

  /**
   * Handles the logout process through Auth0
   */
  const handleLogout = useCallback(async () => {
    try {
      if (isAuthenticationInProgress.current) {
        return;
      }
      isAuthenticationInProgress.current = true;

      resetTokenState();
      resetAddInState();
      useTokenStore.persist.clearStorage();
      useAddInStore.persist.clearStorage();

      const response = await logoutFromAuth0();
      logger.debug("logoutFromAuth0 response", response);

      Office.context.ui.closeContainer();

      // setTimeout(() => {
      //   Office.context.ui.closeContainer();
      // }, 3000);
    } catch (error) {
      clearToken();
      resetAddInState();
      useTokenStore.persist.clearStorage();
      useAddInStore.persist.clearStorage();

      logger.error("Error during logout", error);
      setAuthError(new Error("Error during logout. Please try again."));
    } finally {
      isAuthenticationInProgress.current = false;
    }
  }, []);

  /**
   * Handles the login process through Auth0
   */
  const handleLogin = useCallback(async () => {
    if (!isOfficeReady) {
      logger.error("Cannot login: Office not ready");
      setAuthError(new Error("Office is not ready. Please try again."));
      return;
    } else if (isAuthenticationInProgress.current) {
      return;
    }

    isAuthenticationInProgress.current = true;

    try {
      setAuthError(undefined);
      const response = await loginWithAuth0();
      if (response.token) {
        // set the token in the store and update the authentication state
        setToken(response.token as Token);
        return;
      } else if (response.error) {
        if (response.error instanceof Error) setAuthError(response.error);
      }
    } catch (e) {
      logger.error("Login Error:", e);
      if (e instanceof Error) setAuthError(e);
    } finally {
      isAuthenticationInProgress.current = false;
    }
  }, [isOfficeReady]);

  /**
   * This useEffect handles checking the token expiry and refreshing the token
   * if the token is expired. It also handles the login process if the token
   * refresh fails or if the token is expired and the user needs to login again.
   */
  useEffect(() => {
    if (!isOfficeReady || !isAuthenticated || tokenRefreshed) return;

    const handleTokenRefresh = async () => {
      if (tokenRefreshed) return;

      // reset error state
      setAuthError(undefined);
      setIsRefreshing(true);

      try {
        if (isTokenExpired()) {
          // if the token is expired, we need to login again.
          // This should only happen when the user has authenticated the
          // add-in and the token is expired. Otherwise, we should be able to
          // refresh the token without logging in again.
          logger.warn("Token is expired. Attempting login.");
          throw new Error("Token is expired. Attempting login.");
        }

        // call the refresh token endpoint
        const data = await refreshAuth0Token();

        if (!data.success) {
          throw new Error(
            `Token refresh failed: ${data?.error?.error_description || data?.error?.toString()}`
          );
        } else if (!data.token) {
          throw new Error("Token refresh failed: No token returned");
        }

        // set the new token in the store
        setToken(data.token);

        // set the token refreshed state
        setTokenRefreshed(true);

        logger.debug("Successfully refreshed and stored new tokens");
      } catch (e) {
        logger.error("Error in token refresh", JSON.stringify(e));
        // if (e instanceof Error) setAuthError(e);
        // else setAuthError(new Error("Unknown error in token refresh"));
        setTokenRefreshed(false);

        // attempt tp log the user in again.
        handleLogin();
      } finally {
        setIsRefreshing(false);
      }
    };

    handleTokenRefresh();
  }, [isOfficeReady, isAuthenticated, tokenRefreshed]);

  /**
   * This useEffect handles loading the user data and organizations
   * once the user is authenticated and the Office library is ready.
   */
  useEffect(() => {
    if (!isOfficeReady || !token || !isAuthenticated) return;

    // once the user is authenticated, we need to load the user data
    const loadUserData = async () => {
      try {
        const user = await supabaseApi.login();
        if (!user) return;

        logger.info("User data loaded:", user?.login_email);

        /** set the user profile in the state */
        setUser(user);

        /** fetch the user avatar */
        if (user?.avatar_file_metadata_id) {
          const publicUrl = await supabaseApi.getSupabaseStoragePublicUrl(
            user?.avatar_file_metadata_id
          );
          if (publicUrl) {
            setAvatarUrl(publicUrl);
          } else {
            const streamableLink = await cadchatService.getStreamableLink(
              user?.avatar_file_metadata_id
            );
            if (streamableLink) setAvatarUrl(streamableLink.uri);
          }
        }

        /** check if the user has completed onboarding */
        const completedOnboarding = user?.metadata?.completed_onboarding ?? [];
        if (completedOnboarding.length === 0 || !completedOnboarding.includes("user_profile")) {
          setCurrentView("onboarding");
        }
      } catch (err) {
        logger.error("Error fetching user data", err);
      }
    };

    // once the user is authenticated, we need to load the organizations
    const loadOrganizations = async () => {
      try {
        /** get all organizations */
        const organizations = await supabaseApi.getOrganizations();

        /** filter out organizations without an active subscription */
        const filteredOrganizations =
          await cadchatService.filterOrganizationsWithActiveSubscriptions(organizations);

        /** set the organizations in the state */
        setOrganizations(filteredOrganizations ?? []);
      } catch (err) {
        logger.error("Error fetching organizations", err);
        setOrganizations([]);
      }
    };

    loadUserData().then(() => {
      loadOrganizations();
    });
  }, [isOfficeReady, token, isAuthenticated]);

  // Handle messages from the dialog
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      try {
        if (!event) return;

        const data =
          isString(event?.data) && !event?.data?.includes("webpack")
            ? JSON.parse(event.data)
            : event?.data;

        switch (data.type) {
          case "auth_success":
            logger.debug("Received auth success message:", data);
            await setToken(data.token);
            break;
          case "cadchat_onboarding":
            logger.debug("Received onboarding message:", data);
            break;
          default:
            // logger.debug("Received message from dialog:", data);
            break;
        }
      } catch (error) {
        logger.error("Error parsing massage from dialog:", error);
      }
    };

    Office.onReady(async (info) => {
      logger.debug("Office is ready:", info.host, info.platform);

      window.addEventListener("message", handleMessage);
      setIsOfficeReady(true);
    });

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  if (!isOfficeReady) {
    return (
      <div className={styles.progress_container}>
        <Spinner size="large" />
        <Text>Initializing Office...</Text>
      </div>
    );
  }

  if (isRefreshing) {
    return (
      <div className={styles.progress_container}>
        <Spinner size="large" />
        <Text>Loading authentication...</Text>
      </div>
    );
  }

  if (authError) {
    return (
      <div className={styles.progress_container}>
        <Image
          className={styles.logo}
          src="https://assets.cadchat.com/logos/cadchat/cadchat-horiztonal-color.png"
        />
        <div className={styles.buttons}>
          <Button appearance="primary" onClick={handleLogin} className={styles.login_button}>
            Sign In
          </Button>
        </div>
        <Caption1 className={styles.error_message}>
          {authError?.message || "Authentication Error"}
        </Caption1>
      </div>
    );
  }

  return !token || !isAuthenticated ? (
    <div className={styles.welcome}>
      <Image
        className={styles.welcome_logo}
        src="https://assets.cadchat.com/logos/cadchat/cadchat-horiztonal-color.png"
        alt="CADchat"
      />

      <Body2 className={styles.tagline}>
        CADchat is meeting software for product teams, designed to ensure meetings go beyond just
        talk. It helps teams collaborate effectively, make decisions faster, and keep projects
        moving.
      </Body2>

      <Body2 className={styles.welcome_intro}>
        Schedule CADchat meetings from your Outlook calendar and keep your team in sync — without
        switching between tools.
      </Body2>

      <div className={styles.feature_card}>
        {FEATURES.map((feature) => (
          <div key={feature.text} className={styles.feature_row}>
            <span className={styles.feature_icon}>{feature.icon}</span>
            <Body1 className={styles.feature_text}>{feature.text}</Body1>
          </div>
        ))}
      </div>

      <Button appearance="primary" onClick={handleLogin} className={styles.sign_in_button}>
        Sign In
      </Button>

      <Caption1 className={styles.sign_in_hint}>
        Sign in with your CADchat account to get started.
      </Caption1>
    </div>
  ) : (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        {currentView !== "default" && currentView !== "onboarding" && selectedOrganizationId ? (
          <Button appearance="transparent" icon={<ArrowLeftRegular />} onClick={() => handleBack()}>
            Back
          </Button>
        ) : (
          <Image
            className={styles.header_logo}
            src="https://assets.cadchat.com/logos/cadchat/cadchat-svg/cadchat-logo-horiztonal-opt.svg"
            height="30px"
          />
        )}

        {isAuthenticated ? (
          <Menu positioning={{ autoSize: true }}>
            <MenuTrigger disableButtonEnhancement>
              <MenuButton appearance="transparent" size="small">
                {user && !avatarUrl ? (
                  <Avatar
                    name={
                      user?.first_name
                        ? `${user?.first_name} ${user?.last_name}`
                        : user?.login_email
                    }
                  />
                ) : user && avatarUrl ? (
                  <Avatar
                    image={{
                      src: avatarUrl,
                      alt: user?.first_name
                        ? `${user?.first_name} ${user?.last_name}`
                        : user?.login_email,
                    }}
                  />
                ) : (
                  <Avatar />
                )}
              </MenuButton>
            </MenuTrigger>

            <MenuPopover>
              <MenuList>
                <MenuItem
                  onClick={() => setCurrentView("about")}
                  disabled={!onboardingProgress?.complete}
                >
                  About
                </MenuItem>
                <MenuItem
                  onClick={() => showGettingStartedModal()}
                  disabled={!onboardingProgress?.complete}
                >
                  Help
                </MenuItem>
                {meeting === undefined ? (
                  <MenuItem
                    onClick={() => setCurrentView("settings")}
                    disabled={!onboardingProgress?.complete}
                  >
                    Settings
                  </MenuItem>
                ) : null}
                <MenuDivider />
                <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : null}
      </div>

      {user ? children : null}
    </div>
  );
};
